import { openDB, DBSchema, IDBPDatabase } from "idb";

/**
 * Base local (IndexedDB) para el modo sin conexión:
 *  - "lecturas": última respuesta buena de cada GET, para poder mostrarla
 *    cuando no hay internet (aunque ya no sea la más reciente).
 *  - "pendientes": acciones (crear/editar/eliminar) que se intentaron hacer
 *    sin conexión y quedaron en espera de mandarse en cuanto regrese la señal.
 */
interface OfflineSchema extends DBSchema {
  lecturas: {
    key: string;
    value: { clave: string; datos: any; timestamp: number };
  };
  pendientes: {
    key: number;
    value: {
      id?: number;
      metodo: "post" | "put" | "patch" | "delete";
      url: string;
      datos: any;
      creado: number;
      intentos: number;
      ultimoError?: string;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<OfflineSchema>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<OfflineSchema>("mascotas-vet-offline", 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("lecturas")) {
          db.createObjectStore("lecturas", { keyPath: "clave" });
        }
        if (!db.objectStoreNames.contains("pendientes")) {
          db.createObjectStore("pendientes", { keyPath: "id", autoIncrement: true });
        }
      },
    });
  }
  return dbPromise;
}

// Arma una clave estable para una petición GET a partir de su URL y params,
// para que la misma consulta (ej. /citas?desde=...&hasta=...) siempre
// encuentre/actualice la misma entrada en caché.
export function claveLectura(url: string, params?: Record<string, any>): string {
  const query = params
    ? Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${v}`)
        .join("&")
    : "";
  return query ? `${url}?${query}` : url;
}

export async function guardarLectura(clave: string, datos: any) {
  try {
    const db = await getDb();
    await db.put("lecturas", { clave, datos, timestamp: Date.now() });
  } catch {
    // Si falla el guardado en caché no debe romper la app — es una mejora,
    // no un requisito para que la petición original funcione.
  }
}

export async function obtenerLectura(clave: string) {
  try {
    const db = await getDb();
    return await db.get("lecturas", clave);
  } catch {
    return undefined;
  }
}

export async function encolarPendiente(item: {
  metodo: "post" | "put" | "patch" | "delete";
  url: string;
  datos: any;
}): Promise<number> {
  const db = await getDb();
  return db.add("pendientes", { ...item, creado: Date.now(), intentos: 0 });
}

export async function listarPendientes() {
  const db = await getDb();
  return db.getAll("pendientes");
}

export async function contarPendientes(): Promise<number> {
  const db = await getDb();
  return db.count("pendientes");
}

export async function eliminarPendiente(id: number) {
  const db = await getDb();
  await db.delete("pendientes", id);
}

export async function actualizarIntentoPendiente(id: number, ultimoError: string) {
  const db = await getDb();
  const item = await db.get("pendientes", id);
  if (item) {
    item.intentos += 1;
    item.ultimoError = ultimoError;
    await db.put("pendientes", item);
  }
}
